import { useQuery } from "@tanstack/react-query";
import { useAccessToken } from "@/hooks";
import { UserProfileApi } from "@/openapi/generated";
import { ResponseError } from "@/openapi/generated/runtime";
import { QueryKeys } from "@/lib/types";
import { createAuthenticatedApi } from "@/lib/utils";

interface IUseUserStudentsGetProps {
  userId: number;
  enabled?: boolean;
  /**
   * Admin default stays true for freshness in shared screens.
   * Member portal should pass false — SSR/layout already seed this query.
   * Parent Students page passes "always" so it refetches on each visit.
   */
  refetchOnMount?: boolean | "always";
}

export const useUserStudentsGet = ({
  userId,
  enabled = true,
  refetchOnMount = true,
}: IUseUserStudentsGetProps) => {
  const accessToken = useAccessToken();
  const numericUserId = Number(userId);

  const { data, isSuccess, isPending, isError, refetch, isFetching } = useQuery({
    queryKey: [QueryKeys.UserStudentsGet, numericUserId],
    queryFn: async () => {
      if (!accessToken) {
        throw new Error("Authentication token is required");
      }
      const service = createAuthenticatedApi(UserProfileApi, accessToken);
      try {
        return await service.apiUserProfileIdStudentsGet({ id: numericUserId });
      } catch (error) {
        if (
          error instanceof ResponseError &&
          (error.response.status === 404 || error.response.status === 403)
        ) {
          return [];
        }
        throw error;
      }
    },
    enabled:
      enabled &&
      !!accessToken &&
      Number.isFinite(numericUserId) &&
      numericUserId > 0,
    refetchOnMount,
    refetchOnWindowFocus: false,
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: 10 * 60 * 1000,
  });

  return {
    data,
    isSuccess,
    isError,
    isPending,
    refetch,
    isFetching,
  };
};
